import React, { useState, useEffect, useCallback } from 'react';
import ToastNotification from './ToastNotification';

/**
 * Container that manages multiple toast notifications with mobile optimization
 */
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  
  // Function to add a new toast
  const addToast = useCallback((toast) => {
    const id = Date.now(); // Simple unique ID
    setToasts(prev => [...prev, { ...toast, id }]);
    return id; // Return ID so it can be removed programmatically if needed
  }, []);
  
  // Function to remove a toast by ID
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  
  // Expose methods globally
  useEffect(() => {
    // Create a global toast object
    window.toast = {
      success: (title, message, duration) => 
        addToast({ type: 'success', title, message, duration }),
      error: (title, message, duration) => 
        addToast({ type: 'error', title, message, duration }),
      warning: (title, message, duration) => 
        addToast({ type: 'warning', title, message, duration }),
      info: (title, message, duration) => 
        addToast({ type: 'info', title, message, duration }),
      remove: removeToast
    };
    
    // Cleanup
    return () => {
      delete window.toast;
    };
  }, [addToast, removeToast]);
  
  return (
    <div 
      className="fixed bottom-0 inset-x-0 sm:top-4 sm:right-4 sm:bottom-auto sm:left-auto z-50 flex flex-col items-center sm:items-end gap-2 sm:gap-4 p-2 sm:p-0"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Mobile notification container stacks from bottom on mobile, top-right on larger screens */}
      <div className="w-full sm:w-auto flex flex-col-reverse sm:flex-col items-center sm:items-end gap-2 sm:gap-4">
        {toasts.map(toast => (
          <ToastNotification
            key={toast.id}
            type={toast.type}
            title={toast.title}
            message={toast.message}
            duration={toast.duration || 5000}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
      
      {/* Extra padding at bottom to ensure notifications clear the bottom mobile nav */}
      <div className="h-16 sm:h-0 w-full"></div>
    </div>
  );
};

export default ToastContainer;